import { useQuery } from '@tanstack/react-query';
import {
    fetchMakesByIds,
    fetchModelsByIds,
    fetchDerivativesByIds,
    fetchTransmissionsByIds,
    fetchFuelTypesByIds,
    fetchFuelTypes,
    fetchTransmissions,
} from '../services/lookup';
import type { VehicleNameSummary, DerivativeNameSummary } from '../services/lookup';
import { vehicleSearchKeys } from '../services/queryKeys';

const STALE = 10 * 60 * 1000;
const GC    = 30 * 60 * 1000;

const clean = (ids: (string | null | undefined)[]) =>
    Array.from(new Set(ids.filter((id): id is string => Boolean(id)))).sort();

export const useMakesByIds = (ids: (string | null | undefined)[]) => {
    const unique = clean(ids);
    return useQuery<VehicleNameSummary[]>({
        queryKey: vehicleSearchKeys.makesByIds(unique),
        queryFn: () => fetchMakesByIds(unique),
        enabled: unique.length > 0,
        staleTime: STALE,
        gcTime: GC,
    });
};

export const useModelsByIds = (ids: (string | null | undefined)[]) => {
    const unique = clean(ids);
    return useQuery<VehicleNameSummary[]>({
        queryKey: vehicleSearchKeys.modelsByIds(unique),
        queryFn: () => fetchModelsByIds(unique),
        enabled: unique.length > 0,
        staleTime: STALE,
        gcTime: GC,
    });
};

export const useDerivativesByIds = (ids: (string | null | undefined)[]) => {
    const unique = clean(ids);
    return useQuery<DerivativeNameSummary[]>({
        queryKey: vehicleSearchKeys.derivativesByIds(unique),
        queryFn: () => fetchDerivativesByIds(unique),
        enabled: unique.length > 0,
        staleTime: STALE,
        gcTime: GC,
    });
};

export const useTransmissionsByIds = (ids: (string | null | undefined)[]) => {
    const unique = clean(ids);
    return useQuery<VehicleNameSummary[]>({
        queryKey: vehicleSearchKeys.transmissionsByIds(unique),
        queryFn: () => fetchTransmissionsByIds(unique),
        enabled: unique.length > 0,
        staleTime: STALE,
        gcTime: GC,
    });
};

export const useFuelTypesByIds = (ids: (string | null | undefined)[]) => {
    const unique = clean(ids);
    return useQuery<VehicleNameSummary[]>({
        queryKey: vehicleSearchKeys.fuelTypesByIds(unique),
        queryFn: () => fetchFuelTypesByIds(unique),
        enabled: unique.length > 0,
        staleTime: STALE,
        gcTime: GC,
    });
};

export const useFuelTypes = () =>
    useQuery<VehicleNameSummary[]>({
        queryKey: vehicleSearchKeys.fuelTypes(),
        queryFn: fetchFuelTypes,
        staleTime: STALE,
        gcTime: GC,
    });

export const useTransmissions = () =>
    useQuery<VehicleNameSummary[]>({
        queryKey: vehicleSearchKeys.transmissions(),
        queryFn: fetchTransmissions,
        staleTime: STALE,
        gcTime: GC,
    });

export const buildNameMap = (items: VehicleNameSummary[] | undefined): Map<string, string> => {
    const map = new Map<string, string>();
    (items ?? []).forEach((item) => map.set(item.id, item.name));
    return map;
};

export const buildDerivativeNameMap = (items: DerivativeNameSummary[] | undefined): Map<string, string> => {
    const map = new Map<string, string>();
    (items ?? []).forEach((item) => map.set(item.id, item.short_name));
    return map;
};
